import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { observer } from 'mobx-react-lite'
import { useHistory } from 'react-router-dom'

import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'

import { useApi, useStore } from '../hooks/storeHook'
import { useMessages } from '../hooks/snackbarHooks'

import { appRoutes } from '../common/appRoutes'

import { integrationTypeNames } from '../types/integrationTypeUtils'
import { AdminRealizationReportAccountModel } from '../server/mpsklad_core/Models/AdminRealizationReportAccountModel'

export const AdminRealizationReportsScreen =
  observer(() => {
    const classes = useStyles()

    const {user} = useStore()
    const {admin} = useApi()
    const history = useHistory()
    const {showSuccess, showError} = useMessages()

    const [accounts, setAccounts] = useState<AdminRealizationReportAccountModel[] | null>(null)
    const [filter, setFilter] = useState('')
    const [enqueuingKey, setEnqueuingKey] = useState<string | null>(null)

    const makeAccountKey =
      (account: AdminRealizationReportAccountModel) => `${account.marketplace}-${account.accountId}`

    const loadAccounts =
      useCallback(async () => {
        try {
          setAccounts(await admin.realizationReportAccounts())
        } catch (e) {
          console.error('Failed to load realization report accounts', e)
        }
      }, [admin])

    useEffect(() => {
      loadAccounts().catch(console.error)
    }, [loadAccounts])

    if (!user?.isSuperAdmin) {
      return null
    }

    // TODO: Use MemoryTableData
    const filteredAccounts =
      useMemo<AdminRealizationReportAccountModel[]>(() => {
          if (!accounts?.length) {
            return []
          }

          if (!filter) {
            return accounts
          }

          const filterLower = filter.toLowerCase()

          return accounts.filter(account => account.userEmail.toLowerCase().includes(filterLower)
                                            || account.accountName.toLowerCase().includes(filterLower))
        },
        [accounts, filter])

    const handleEnqueue =
      async (account: AdminRealizationReportAccountModel) => {
        const key = makeAccountKey(account)

        if (key === enqueuingKey) {
          return
        }

        setEnqueuingKey(key)

        try {
          await admin.enqueueRealizationReport({
            marketplace: account.marketplace,
            accountId: account.accountId,
            userId: account.userId
          })
          showSuccess('Задача поставлена в очередь')
        } catch (e) {
          console.error('Failed to enqueue realization report', e)
          showError('Не удалось поставить задачу в очередь')
        } finally {
          setEnqueuingKey(null)
        }
      }

    return (
      <div className="body_wrapper_zero">
        <div className="body_wrapper">
          <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" marginBottom={2}>
            <h1 className={classes.title}>
              Отчёты о реализации
            </h1>

            <Box display="flex" flexWrap="wrap" alignItems="center">
              <Button
                className={classes.navButton}
                variant="outlined"
                onClick={() => history.push(appRoutes.AdminUsers)}>
                Пользователи
              </Button>
            </Box>
          </Box>
        </div>

        <div className="body_wrapper">
          <Box display="flex" alignItems="center">
            <input
              autoFocus
              className="main_input"
              type="text"
              placeholder="Поиск по email или аккаунту"
              value={filter}
              onChange={e => setFilter(e.target.value)}
            />
          </Box>
        </div>

        <div className="body_wrapper">
          {
            accounts === null
            ? <CircularProgress/>
            :
            <Paper>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Email</TableCell>
                    <TableCell>Аккаунт</TableCell>
                    <TableCell>Маркетплейс</TableCell>
                    <TableCell>Запустить</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {
                    filteredAccounts.length === 0
                    ?
                    <TableRow>
                      <TableCell colSpan={4}>
                        <Typography variant="body2" color="textSecondary">
                          {filter ? 'Не найдено' : 'Нет аккаунтов с включёнными отчётами о реализации'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                    :
                    filteredAccounts.map(account => {
                      const key = makeAccountKey(account)
                      const isEnqueuing = enqueuingKey === key

                      return (
                        <TableRow key={key}>
                          <TableCell>{account.userEmail}</TableCell>

                          <TableCell>{account.accountName}</TableCell>

                          <TableCell>{integrationTypeNames[account.marketplace]}</TableCell>

                          <TableCell>
                            <Button
                              size="small"
                              variant="outlined"
                              disabled={isEnqueuing}
                              onClick={() => handleEnqueue(account)}>
                              {isEnqueuing ? <CircularProgress size={16}/> : 'Запустить'}
                            </Button>
                          </TableCell>
                        </TableRow>
                      )
                    })}
                </TableBody>
              </Table>
            </Paper>
          }
        </div>
      </div>
    )
  })

const useStyles =
  makeStyles(
    ({spacing}) => ({
      title: {
        margin: 0
      },
      navButton: {
        marginRight: spacing(1),
        marginBottom: spacing(1)
      }
    }))